<template>
    <div
        class="relative flex h-56 w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-100 transition-colors hover:border-slate-400 hover:bg-slate-200"
        @click="openModal"
    >
        <template v-if="selected">
            <img
                :src="selected.url"
                class="h-full w-full rounded-lg object-contain"
                :alt="selected.name"
            />
            <div
                class="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 opacity-0 transition-opacity hover:opacity-100"
            >
                <span class="text-sm font-medium text-white">{{
                    $t('change_image')
                }}</span>
            </div>
        </template>
        <template v-else>
            <svg
                class="mb-1 h-6 w-6 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
            </svg>
            <p class="text-sm text-slate-500">
                {{ $t('click_to_choose_image') }}
            </p>
        </template>
    </div>

    <Teleport to="body">
        <div
            v-if="isOpen"
            class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50"
            @click.self="closeModal"
        >
            <div
                class="flex h-[70vh] w-[60vw] flex-col rounded-xl bg-white shadow-xl"
            >
                <!-- Header -->
                <div
                    class="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4"
                >
                    <h2 class="text-base font-semibold text-slate-800">
                        {{ $t('media_library') }}
                    </h2>
                    <div class="flex items-center gap-2">
                        <button
                            type="button"
                            class="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
                            :disabled="isUploading"
                            @click="fileInputRef?.click()"
                        >
                            <svg
                                v-if="isUploading"
                                class="h-4 w-4 animate-spin"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"
                                />
                                <path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v8H4z"
                                />
                            </svg>
                            <svg
                                v-else
                                class="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                            {{
                                isUploading ? $t('uploading') : $t('upload_new')
                            }}
                        </button>
                        <button
                            type="button"
                            class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                            @click="closeModal"
                        >
                            <svg
                                class="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                <!-- Body -->
                <div class="flex-1 overflow-y-auto p-5">
                    <!-- Loading -->
                    <div
                        v-if="isFetching"
                        class="flex h-full items-center justify-center"
                    >
                        <svg
                            class="h-8 w-8 animate-spin text-slate-400"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            />
                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v8H4z"
                            />
                        </svg>
                    </div>

                    <!-- Empty state -->
                    <div
                        v-else-if="assets.length === 0"
                        class="flex h-full flex-col items-center justify-center gap-3 text-center"
                    >
                        <div
                            class="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100"
                        >
                            <svg
                                class="h-8 w-8 text-slate-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="1.5"
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                        </div>
                        <div>
                            <p class="text-sm font-medium text-slate-700">
                                {{ $t('no_images_yet') }}
                            </p>
                            <p class="mt-0.5 text-xs text-slate-400">
                                {{
                                    $t(
                                        'upload_your_first_image_using_the_button_above'
                                    )
                                }}
                            </p>
                        </div>
                    </div>

                    <!-- Grid -->
                    <div
                        v-else
                        class="grid grid-cols-4 gap-3"
                    >
                        <div
                            v-for="asset in assets"
                            :key="asset.id"
                            class="group relative aspect-square cursor-pointer overflow-hidden rounded-lg border-2 transition-all"
                            :class="
                                selected?.id === asset.id
                                    ? 'border-blue-500 ring-2 ring-blue-200'
                                    : 'border-transparent hover:border-slate-300'
                            "
                            @click="selectAsset(asset)"
                        >
                            <img
                                :src="asset.url"
                                :alt="asset.alt_text || asset.name"
                                class="h-full w-full object-cover"
                            />
                            <button
                                type="button"
                                class="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity hover:bg-black/70 group-hover:opacity-100"
                                :title="$t('edit_alt_text')"
                                @click.stop="startAltEdit(asset)"
                            >
                                <svg
                                    class="h-3 w-3 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                </svg>
                            </button>
                            <div
                                v-if="editingAssetId === asset.id"
                                class="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-white/95 p-1.5"
                                @click.stop
                            >
                                <input
                                    v-model="altDraft"
                                    type="text"
                                    class="form-input w-full !py-1 text-xs"
                                    :placeholder="$t('alt_text')"
                                    @keyup.enter="saveAltEdit(asset)"
                                    @keyup.esc="cancelAltEdit"
                                />
                                <button
                                    type="button"
                                    class="shrink-0 text-xs font-medium text-blue-600"
                                    @click="saveAltEdit(asset)"
                                >
                                    {{ $t('save') }}
                                </button>
                            </div>
                            <div
                                v-else
                                class="absolute inset-0 flex items-end bg-gradient-to-t from-black/50 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100"
                            >
                                <span class="truncate text-xs text-white">{{
                                    asset.name
                                }}</span>
                            </div>
                            <div
                                v-if="selected?.id === asset.id"
                                class="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500"
                            >
                                <svg
                                    class="h-3 w-3 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="3"
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div
                    class="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 px-5 py-3"
                >
                    <button
                        type="button"
                        class="rounded-lg px-4 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-100"
                        @click="closeModal"
                    >
                        {{ $t('cancel') }}
                    </button>
                    <button
                        type="button"
                        class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                        :disabled="!selected"
                        @click="confirmSelection"
                    >
                        {{ $t('select') }}
                    </button>
                </div>
            </div>
        </div>
    </Teleport>

    <input
        ref="fileInputRef"
        type="file"
        class="hidden"
        accept="image/jpeg,image/png,image/webp,image/svg+xml,image/gif"
        @change="onFileChange"
    />
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { api } from '@src/utils/api/client'
import { uploadPublic } from '@src/utils/api/s3'
import type { MediaAsset } from '@builder/types'

const emit = defineEmits<{
    (e: 'select', asset: MediaAsset): void
}>()

const isOpen = ref(false)
const isFetching = ref(false)
const isUploading = ref(false)
const assets = ref<MediaAsset[]>([])
const selected = ref<MediaAsset | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)
const editingAssetId = ref<string | null>(null)
const altDraft = ref('')

const fetchAssets = async () => {
    isFetching.value = true
    try {
        const { data } = await api.get('manage/builder/media')
        assets.value = data.data
    } finally {
        isFetching.value = false
    }
}

const openModal = () => {
    selected.value = null
    isOpen.value = true
    fetchAssets()
}

const closeModal = () => {
    isOpen.value = false
}

const selectAsset = (asset: MediaAsset) => {
    selected.value = asset
}

const confirmSelection = () => {
    if (!selected.value) return
    emit('select', selected.value)
    closeModal()
}

const startAltEdit = (asset: MediaAsset) => {
    editingAssetId.value = asset.id
    altDraft.value = asset.alt_text ?? ''
}

const cancelAltEdit = () => {
    editingAssetId.value = null
}

const saveAltEdit = async (asset: MediaAsset) => {
    const { data } = await api.patch<{ data: MediaAsset }>(
        `manage/builder/media/${asset.id}`,
        { alt_text: altDraft.value }
    )
    asset.alt_text = data.data.alt_text
    editingAssetId.value = null
}

const onFileChange = async (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (!file) return

    isUploading.value = true
    try {
        const { data: uploadData } = await uploadPublic(file)
        const { data: assetResponse } = await api.post<{ data: MediaAsset }>(
            'manage/builder/media',
            {
                upload_key: uploadData.key,
                name: file.name.replace(/\.[^.]+$/, ''),
            }
        )
        assets.value.unshift(assetResponse.data)
        selected.value = assetResponse.data
    } finally {
        isUploading.value = false
        if (fileInputRef.value) fileInputRef.value.value = ''
    }
}

defineExpose({ openModal })
</script>
